'use client';

import { useState, useMemo, useEffect } from 'react';
import styles from './PricingCalculator.module.css';
import { 
  Check, Plus, Minus, Send, MessageSquare, Users, Cloud, 
  Zap, Target, DollarSign, ShoppingCart, Truck, Wrench, Search,
  AlertTriangle, Crown, Sparkles, Settings
} from 'lucide-react';

// ==========================================
// TIPAGEM PARA BLINDAR O TYPESCRIPT
// ==========================================
type PresetPack = {
  id: string;
  name: string;
  icon: React.ElementType;
  badge?: string;
  baseModules?: string[];
  bonusModules?: string[];
  fixedAddons?: Record<string, number>;
};

const defaultPrices = {
  modules: [
    { id: 'sales', name: 'Gestão de Vendas & PDV', price: 100, icon: ShoppingCart },
    { id: 'finance', name: 'Gestão Financeira', price: 100, icon: DollarSign },
    { id: 'aesir_chat', name: 'Aesir Chat (Atendimento)', price: 100, icon: MessageSquare },
    { id: 'campaigns', name: 'Campanhas (WhatsApp)', price: 100, icon: Target },
    { id: 'crm', name: 'Aesir CRM (Pipelines)', price: 100, icon: Users },
    { id: 'automations', name: 'Motor de Automações', price: 20, icon: Zap },
    { id: 'lead_search', name: 'Busca de Leads (Maps)', price: 10, icon: Search },
    { id: 'hr', name: 'Recursos Humanos', price: 40, icon: Users },
    { id: 'logistics', name: 'Logística e Entregas', price: 40, icon: Truck },
    { id: 'service_orders', name: 'Ordens de Serviço (OS)', price: 40, icon: Wrench },
    { id: 'aesir_cloud', name: 'Aesir Cloud (NOC)', price: 100, icon: Cloud },
  ],
  addons: [
    { id: 'user_license', name: 'Usuário Extra (ERP)', price: 5, step: 1, min: 1 },
    { id: 'extra_instance', name: 'Instância Adicional (Whats)', price: 10, step: 1, min: 0 },
    { id: 'official_api', name: 'Addon WhatsApp Official', price: 20, step: 1, min: 0 },
    { id: 'agent_license', name: 'Atendente Extra (Chat)', price: 5, step: 1, min: 0 },
    { id: 'lead_credits', name: 'Pacote de Créditos (Leads)', price: 1, step: 100, min: 0 },
  ]
};

// ==========================================
// OS NOVOS PACOTES ESTRATÉGICOS (Tipados)
// ==========================================
const PRESET_PACKS: Record<string, PresetPack> = {
  starter: { 
    id: 'starter', name: 'Plano Disparo', icon: Target, badge: 'Mais Barato',
    baseModules: ['campaigns'],
    bonusModules: [], 
    fixedAddons: { 'user_license': 1, 'extra_instance': 1 }
  },
  essential: { 
    id: 'essential', name: 'Plano Atendimento', icon: Sparkles, badge: 'Custo x Benefício',
    baseModules: ['sales', 'campaigns'],
    bonusModules: ['aesir_chat', 'crm'],
    fixedAddons: { 'user_license': 3, 'extra_instance': 2 }
  },
  premium: { 
    id: 'premium', name: 'Ecossistema Total', icon: Crown, badge: 'Completo',
    baseModules: ['sales', 'finance', 'aesir_chat', 'campaigns', 'crm'],
    bonusModules: ['automations', 'lead_search', 'hr'],
    fixedAddons: { 'user_license': 5, 'extra_instance': 5 }
  },
  custom: { 
    id: 'custom', name: 'Montar meu Plano', icon: Settings, badge: '' 
  }
};

export function PricingCalculator() {
  const [activePack, setActivePack] = useState<string>('essential');
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());
  const [addons, setAddons] = useState<Record<string, number>>({});

  useEffect(() => {
    if (activePack === 'custom') return;

    const packData = PRESET_PACKS[activePack];
    if (packData && packData.baseModules && packData.bonusModules) {
      const allMods = new Set([...packData.baseModules, ...packData.bonusModules]);
      setSelectedModules(allMods);
      setAddons({ ...packData.fixedAddons });
    }
  }, [activePack]);

  const getBonusModules = () => {
    if (activePack === 'custom') return [];
    const packData = PRESET_PACKS[activePack];
    return packData.bonusModules || [];
  };

  const bonusModulesList = getBonusModules();

  const toggleModule = (id: string) => {
    if (activePack !== 'custom') {
      const packData = PRESET_PACKS[activePack];
      if (packData.baseModules?.includes(id) || packData.bonusModules?.includes(id)) {
        return; 
      }
    }

    const newSelected = new Set(selectedModules);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedModules(newSelected);
  };

  const updateAddon = (id: string, amount: number, min: number = 0) => {
    setAddons(prev => {
      let realMin = min;
      if (activePack !== 'custom') {
        const packData = PRESET_PACKS[activePack];
        if (packData.fixedAddons && packData.fixedAddons[id] !== undefined) {
           realMin = packData.fixedAddons[id];
        }
      }

      const current = prev[id] || 0;
      const next = Math.max(realMin, current + amount);
      return { ...prev, [id]: next };
    });
  };

  const requiresWhatsApp = selectedModules.has('aesir_chat') || selectedModules.has('campaigns');
  const hasWhatsAppInstance = (addons['extra_instance'] || 0) > 0 || (addons['official_api'] || 0) > 0;
  
  const warnings = useMemo(() => {
    const alerts = [];
    if (selectedModules.size === 0) alerts.push("Selecione pelo menos 1 módulo.");
    if ((addons['user_license'] || 0) < 1) alerts.push("É necessário pelo menos 1 licença de usuário.");
    if (requiresWhatsApp && !hasWhatsAppInstance) alerts.push("Módulos de Chat/Campanhas exigem 1 Instância de WhatsApp.");
    return alerts;
  }, [selectedModules, addons, requiresWhatsApp, hasWhatsAppInstance]);

  const isValid = warnings.length === 0;

  const calculateTotals = () => {
    let totalToPay = 0;
    let totalSaved = 0;

    defaultPrices.modules.forEach(m => {
      if (selectedModules.has(m.id)) {
        if (bonusModulesList.includes(m.id)) {
          totalSaved += m.price;
        } else {
          totalToPay += m.price;
        }
      }
    });

    defaultPrices.addons.forEach(a => {
      const qty = addons[a.id] || 0;
      totalToPay += (qty * a.price);
    });

    return { totalToPay, totalSaved };
  };

  const { totalToPay, totalSaved } = calculateTotals();

  const handleWhatsAppRedirect = () => {
    if (!isValid) return;
    const packName = PRESET_PACKS[activePack].name;
    const modNames = defaultPrices.modules.filter(m => selectedModules.has(m.id) && !bonusModulesList.includes(m.id)).map(m => m.name).join(', ');
    const bonusNames = defaultPrices.modules.filter(m => bonusModulesList.includes(m.id)).map(m => m.name).join(', ');
    const addNames = defaultPrices.addons.filter(a => addons[a.id] > 0).map(a => `${addons[a.id]}x ${a.name}`).join(', ');
    
    const text = `Olá! Tenho interesse no *${packName}* do Aesir ERP.\n\n*Módulos Pagos:* ${modNames || 'Nenhum'}\n*Módulos Bônus:* ${bonusNames || 'Nenhum'}\n*Expansões:* ${addNames || 'Nenhum'}\n\n*Total:* R$ ${totalToPay.toFixed(2)}/mês.\n\nGostaria de fechar este pacote!`;
    window.open(`https://api.whatsapp.com/send?phone=5511983493522&text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div>
      {/* ABAS DE PACOTES COM BADGES */}
      <div className={styles.planTabs}>
        {Object.values(PRESET_PACKS).map(pack => (
          <button 
            key={pack.id} 
            className={`${styles.tabBtn} ${activePack === pack.id ? styles.active : ''}`}
            onClick={() => setActivePack(pack.id)}
          >
            {pack.badge && (
              <span className={`${styles.tabBadge} ${pack.id === 'essential' ? styles.badgeGreen : styles.badgeOrange}`}>
                {pack.badge}
              </span>
            )}
            <pack.icon size={18} />
            {pack.name}
          </button>
        ))}
      </div>

      <div className={styles.calculatorWrapper}>
        <div className={styles.modulesSection}>
          
          <div className={styles.sectionHeader}>
            <h3>1. Módulos do Sistema</h3>
            <p>{activePack === 'custom' ? 'Escolha o que a sua empresa precisa.' : 'Este pacote já inclui a estrutura essencial para operar.'}</p>
          </div>
          
          <div className={styles.grid}>
            {defaultPrices.modules.map(mod => {
              const Icon = mod.icon;
              const isSelected = selectedModules.has(mod.id);
              const isBonus = bonusModulesList.includes(mod.id);
              
              let cardClass = styles.moduleCard;
              if (isSelected) cardClass += ` ${styles.active}`;
              if (isBonus) cardClass += ` ${styles.freeBonus}`;
              else if (activePack !== 'custom' && isSelected) cardClass += ` ${styles.lockedPack}`;

              return (
                <div key={mod.id} className={cardClass} onClick={() => toggleModule(mod.id)}>
                  <div className={styles.cardHeader}>
                    <div className={styles.iconWrapper}><Icon size={20} /></div>
                    <div className={styles.checkbox}>{isSelected && <Check size={14} strokeWidth={4} />}</div>
                  </div>
                  <div className={styles.modInfo}>
                    <span className={styles.modName}>{mod.name}</span>
                    <div className={styles.priceWrapper}>
                      {isBonus ? (
                        <>
                          <span className={styles.priceStrikethrough}>R$ {mod.price.toFixed(2)}</span>
                          <span className={styles.priceFree}>Grátis</span>
                        </>
                      ) : (
                        <span className={styles.modPrice}>R$ {mod.price.toFixed(2)}/mês</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.sectionHeader} style={{ marginTop: '3rem' }}>
            <h3>2. Expansões e Usuários</h3>
            <p>Os limites abaixo já acompanham o seu pacote.</p>
          </div>
          
          <div className={styles.addonsList}>
            {defaultPrices.addons.map(addon => {
              const isMissingInstance = requiresWhatsApp && !hasWhatsAppInstance && (addon.id === 'extra_instance' || addon.id === 'official_api');
              
              let currentMin = addon.min;
              if (activePack !== 'custom') {
                const packData = PRESET_PACKS[activePack];
                if (packData.fixedAddons && packData.fixedAddons[addon.id] !== undefined) {
                  currentMin = packData.fixedAddons[addon.id];
                }
              }

              const isMinusDisabled = (addons[addon.id] || 0) <= currentMin;

              return (
                <div key={addon.id} className={`${styles.addonRow} ${isMissingInstance ? styles.requiredHighlight : ''}`}>
                  <div className={styles.addonInfo}>
                    <span className={styles.addonName}>{addon.name}</span>
                    <span className={styles.addonPrice}>+ R$ {addon.price.toFixed(2)} {addon.step > 1 ? `(Pacote c/ ${addon.step})` : '/cada'}</span>
                  </div>
                  <div className={styles.counter}>
                    <button onClick={() => updateAddon(addon.id, -1, currentMin)} disabled={isMinusDisabled}>
                      <Minus size={16} />
                    </button>
                    <span>{addons[addon.id] || 0}</span>
                    <button onClick={() => updateAddon(addon.id, 1, currentMin)}>
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* PAINEL RESUMO (DIREITA) */}
        <div className={styles.summarySection}>
          <div className={styles.summarySticky}>
            <div className={styles.summaryHeader}>
              <h4>Resumo do Plano</h4>
            </div>
            
            <div className={styles.summaryBody}>
              <ul className={styles.summaryList}>
                {defaultPrices.modules.filter(m => selectedModules.has(m.id) && !bonusModulesList.includes(m.id)).map(m => (
                  <li key={m.id}><span>{m.name}</span> <span>R$ {m.price.toFixed(2)}</span></li>
                ))}
                
                {defaultPrices.modules.filter(m => selectedModules.has(m.id) && bonusModulesList.includes(m.id)).map(m => (
                  <li key={m.id} style={{color: '#10b981'}}>
                    <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}><Sparkles size={14}/> {m.name}</span> 
                    <span>Bônus</span>
                  </li>
                ))}

                {defaultPrices.addons.filter(a => addons[a.id] > 0).map(a => (
                  <li key={a.id} className={styles.addonItem}>
                    <span>{addons[a.id]}x {a.name}</span> 
                    <span>R$ {(addons[a.id] * a.price).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.summaryFooter}>
              
              {totalSaved > 0 && (
                <div className={styles.savingsBox}>
                  <p>🎁 Você está ganhando de bônus:</p>
                  <h5>R$ {totalSaved.toFixed(2)} / mês</h5>
                </div>
              )}

              {warnings.length > 0 && (
                <div className={styles.validationBox}>
                  <div className={styles.validationHeader}>
                    <AlertTriangle size={16} /><span>Atenção aos Requisitos:</span>
                  </div>
                  <ul>{warnings.map((warn, i) => <li key={i}>{warn}</li>)}</ul>
                </div>
              )}

              <div className={styles.totalRow}>
                <span>Investimento Mensal</span>
                <span className={styles.totalPrice}>R$ {totalToPay.toFixed(2)}</span>
              </div>
              
              <button className={styles.ctaButton} onClick={handleWhatsAppRedirect} disabled={!isValid}>
                <Send size={18} /> Quero este plano
              </button>
              <p className={styles.disclaimer}>Sem fidelidade. Cancele a qualquer momento.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}